package diana.soleil.design.iraniancanadienbazzar.core.data

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOf

/**
 * App-wide network reachability, driving the shell's offline banner. [source] is the platform's
 * connectivity stream — Android `ConnectivityManager`, iOS `NWPathMonitor`, web `online`/`offline`
 * events — injected from the app entry point exactly like location/image-pick lambdas; `null`
 * (previews, tests) means "always online" so nothing changes for fakes.
 *
 * [setForcedOffline] is a debug-only override registered in the Developer Menu
 * (`docs/DEVELOPER_MENU.md`) so QA can exercise the offline UI without airplane mode — it only
 * forces the banner ON; it never fakes being online.
 */
class ConnectivityController(private val source: Flow<Boolean>? = null) {

    private val forcedOffline = MutableStateFlow(false)

    /** Emits false while the device is offline OR the debug override forces the banner. */
    val isOnline: Flow<Boolean> =
        combine(source ?: flowOf(true), forcedOffline) { online, forced -> online && !forced }
            .distinctUntilChanged()

    fun isForcedOffline(): Boolean = forcedOffline.value

    fun setForcedOffline(value: Boolean) {
        forcedOffline.value = value
    }
}
